import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { useCopilotEntryOn } from "../../src/taskpane/components/copilot/useCopilotEntryEnabled";
import { closeCopilotPane, COPILOT_PANE_DEFAULT_WIDTH, COPILOT_PANE_MIN_WIDTH } from "../../src/taskpane/redux/modules/copilot";
import { CopilotAvailability } from "../../src/taskpane/services/copilot/copilotAvailability";
import { ScreenSize, useWindowSize } from "../Hooks/useWindowSize";

/** What the docked pane takes from the row besides its width: the outer stack's 20 px gap before
 * its handle, and the handle (5 px and a 1 px border). The pane sits flush against the handle:
 * CopilotPane takes the stack's gap off it. */
export const COPILOT_PANE_GUTTER: number = 26;
/** Room the body keeps beside the docked pane. */
export const COPILOT_BODY_MIN_WIDTH: number = 400;

/** What sits left of the body in a shell (ContentManager.tsx, contentManagerTeams.tsx). */
export interface ICopilotBodyLayout {
    /** The app's left padding: 20 in ?embed=1 and a Teams channel tab, 10 in Teams site mode, 0 otherwise. */
    appPaddingLeft: number;
    /** The side menu shows (not in ?embed=1 or a Teams channel tab). */
    hasSideMenu: boolean;
    isSideMenuCollapsed: boolean;
    /** The open side menu's width: the shell's resizable `width` state (350 px or more). */
    sideMenuWidth: number;
}

/** Where the body starts, from the shells' styles: the app's padding, then the side menu. Open, the
 * menu's width, its 5 px handle with a 1 px border and a 5 px margin, and the outer stack's 20 px
 * gap; collapsed, 35 px with a 1 px border and 10 px margins either side, and that gap. */
const bodyLeftOf = (layout?: ICopilotBodyLayout): number => !layout
    ? 0
    : layout.appPaddingLeft + (!layout.hasSideMenu ? 0 : layout.isSideMenuCollapsed ? 76 : layout.sideMenuWidth + 31);

export interface IContentManagerCopilot {
    availability: CopilotAvailability;
    /** The entry points show: available, and not an Essentials user (the content manager stays closed to them). */
    enabled: boolean;
    showPane: boolean;
    /** Beside the body above 768 px; over it at 768 px and below, or where the side menu leaves no room. */
    isDocked: boolean;
    /** The docked pane's width: the stored width, narrowed so the body keeps COPILOT_BODY_MIN_WIDTH,
     * and never under COPILOT_PANE_MIN_WIDTH. */
    paneWidth: number;
    /** Pixels ContentManagerBody gives up for the docked pane. */
    inset: number;
    /** The inset with the side menu collapsed, so it does not change when the menu toggles: what
     * the shells size the body's room by. Sized by inset, expanding the menu narrowed the pane, the
     * room crossed a size, and the menu collapsed again at once (audit 2026-10-19). */
    insetIfCollapsed: number;
}

/**
 * Whether the content manager offers Copilot (available, and not an Essentials user), without the
 * pane's layout and so without its window-size listener: for the library grid, which needs only this
 * (final review 2026-10-22).
 */
export const useContentManagerCopilotEnabled = (): { availability: CopilotAvailability; enabled: boolean } => {
    // The host-aware rule lives in useCopilotEntryOn: here the content manager's (closed to Essentials
    // users whatever the licence says, so no Graph lookup for it; audit 2026-10-19).
    return useCopilotEntryOn(true);
};

/**
 * The content manager's Copilot pane state (spec §6). The shells pass what sits left of the body, so
 * a wide stored width (dragged, or saved on a bigger screen) or a narrow window never pushes the
 * pane and its close button off-screen; the header's button passes nothing (it reads no width).
 */
export const useContentManagerCopilot = (layout?: ICopilotBodyLayout): IContentManagerCopilot => {
    const { availability, enabled } = useContentManagerCopilotEnabled();
    const isPaneOpen: boolean = useSelector((state: RootState) => state.copilot?.isPaneOpen === true);
    const storedWidth: number = useSelector((state: RootState) => state.copilot?.paneWidth ?? COPILOT_PANE_DEFAULT_WIDTH);
    const { width: windowWidth, screenSize } = useWindowSize();

    const showPane: boolean = enabled && isPaneOpen;
    const measure = (bodyLayout?: ICopilotBodyLayout): { isDocked: boolean; paneWidth: number; inset: number } => {
        // Room right of the body's start for the gutter, the body's minimum and the pane.
        const room: number = windowWidth - bodyLeftOf(bodyLayout) - COPILOT_PANE_GUTTER;
        const paneWidth: number = Math.max(COPILOT_PANE_MIN_WIDTH, Math.min(storedWidth, room - COPILOT_BODY_MIN_WIDTH));
        const isDocked: boolean = screenSize !== ScreenSize.XXSmall && room >= COPILOT_PANE_MIN_WIDTH;
        return { isDocked, paneWidth, inset: showPane && isDocked ? paneWidth + COPILOT_PANE_GUTTER : 0 };
    };
    const actual = measure(layout);
    const ifCollapsed = layout ? measure({ ...layout, isSideMenuCollapsed: true }) : actual;
    return { availability, enabled, showPane, isDocked: actual.isDocked, paneWidth: actual.paneWidth, inset: actual.inset, insetIfCollapsed: ifCollapsed.inset };
};

/**
 * A search run while the pane covers the body (at 768 px and below, or where the side menu leaves no
 * room) closes the pane, so the results show: they ran unseen behind it (audit 2026-10-19). A docked
 * pane stays. The shells call this with their own useContentManagerCopilot result.
 */
export const useCopilotOverlayStepsAsideForSearch = (copilot: IContentManagerCopilot): void => {
    const dispatch = useDispatch();
    const searchValue: string = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue?.value ?? "");
    const isOverlayRef = useRef<boolean>(false);
    isOverlayRef.current = copilot.showPane && !copilot.isDocked;
    useEffect(() => {
        if (searchValue && isOverlayRef.current) {
            dispatch(closeCopilotPane());
        }
    }, [searchValue]);
};
